import * as React from 'react';
import { useTheme } from '@emotion/react';
import { useIntl } from 'react-intl';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TranslationKey } from '@/locales/fr';
import { useGetFilesQuery, useUploadFileMutation } from '@/services/innovorder/library/library.endpoints';
import { ImageInputProps, Sections, ImageInputActionType } from './ImageInput.interface';
import { GRID_ROW_HEIGHT, GRID_ROW_ITEMS, TEXT_HEIGHT } from './ImageInput.style';
import { reducer } from './ImageInput.reducer';

export const SECTION_CONFIG: Record<Sections, TranslationKey> = {
    [Sections.Library]: 'imageInput.section.library.title',
    [Sections.Upload]: 'imageInput.section.upload.title',
};

const PAGE_SIZE = 100;

export const useImageInputVM = (props: ImageInputProps) => {
    const { brandId, restaurantId, value, defaultImage, onChange, maxFileSize, validateFile, disableResize } = props;

    // At least one of the two is defined
    const imageContextIdProps = React.useMemo(
        () => (restaurantId ? { restaurantId } : { brandId: brandId as number }),
        [brandId, restaurantId],
    );

    const intl = useIntl();

    const theme = useTheme();

    const [state, dispatch] = React.useReducer(reducer, {
        isModalOpen: false,
        currentSection: Sections.Library,
        isUploadLoading: false,
        page: 0,
        search: '',
        resize: false,
    });
    const { page, isModalOpen, currentSection, search, isUploadLoading, resize } = state;

    const handleOpenModal = React.useCallback(
        () => dispatch({ type: ImageInputActionType.TOGGLE_MODAL, payload: true }),
        [],
    );
    const handleCloseModal = React.useCallback(
        () => dispatch({ type: ImageInputActionType.TOGGLE_MODAL, payload: false }),
        [],
    );

    const handleResizeImageChanged = React.useCallback(
        () => dispatch({ type: ImageInputActionType.SWITCH_RESIZE }),
        [],
    );

    const [handleUploadImageMutation] = useUploadFileMutation();
    const [uploadedImage, setUploadedImage] = React.useState<{ imageId: number; fullUrl: string } | null>(null);
    const [uploadError, setUploadError] = React.useState<string | null>(null);
    const handleUploadImage = React.useCallback(
        async (files: File[]) => {
            if (maxFileSize && files.some((f) => f.size > maxFileSize)) {
                setUploadError('imageInput.section.upload.fileTooLarge');
                return;
            }
            if (validateFile) {
                const error = await validateFile(files[0]);
                if (error) {
                    setUploadError(error);
                    return;
                }
            }
            setUploadError(null);
            dispatch({ type: ImageInputActionType.SET_UPLOAD_LOADING, payload: true });
            const [response] = await Promise.all(
                files.map((file) =>
                    handleUploadImageMutation({
                        file,
                        resize: disableResize ? false : !resize,
                        ...imageContextIdProps,
                    }),
                ),
            );
            dispatch({ type: ImageInputActionType.SET_UPLOAD_LOADING, payload: false });
            if ('data' in response && response.data.imageId) {
                setUploadedImage({ imageId: response.data.imageId, fullUrl: response.data.fullUrl });
                onChange?.(response.data.imageId);
            }
        },
        [handleUploadImageMutation, imageContextIdProps, onChange, resize, maxFileSize, validateFile, disableResize],
    );

    const handleImageClick = (image: { imageId: number }) => () => {
        if (onChange) {
            onChange(image.imageId);
        }
        handleCloseModal();
    };

    // TODO: useDeferedValue for search
    const getFilesResponse = useGetFilesQuery(
        {
            ...imageContextIdProps,
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
            search: !search.length ? undefined : search,
        },
        { skip: !isModalOpen },
    );

    const allFilesPaginatedData = React.useRef<(typeof getFilesResponse.data)[]>([]);
    allFilesPaginatedData.current[page] = getFilesResponse.data ?? [];

    const hasNextPage = getFilesResponse.data?.length === PAGE_SIZE;
    const virtualListRef = React.useRef<HTMLDivElement>(null);
    const libraryFiles = allFilesPaginatedData.current.flat();

    const rowVirtualizer = useVirtualizer({
        count: Math.ceil((hasNextPage ? libraryFiles.length + 1 : libraryFiles.length) / GRID_ROW_ITEMS),
        getScrollElement: () => virtualListRef.current,
        estimateSize: () => TEXT_HEIGHT + GRID_ROW_HEIGHT + Number(theme.spacing[2].replace('px', '')),
        overscan: 5,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();
    React.useEffect(() => {
        const lastItem = virtualItems[virtualItems.length - 1];
        if (!lastItem) {
            return;
        }

        if (lastItem.index >= libraryFiles.length / GRID_ROW_ITEMS - 1 && hasNextPage && !getFilesResponse.isFetching) {
            dispatch({ type: ImageInputActionType.NEXT_PAGE });
        }
    }, [hasNextPage, libraryFiles.length, virtualItems, getFilesResponse.isFetching]);

    const handleSetSection = React.useCallback(
        (payload: Sections) => dispatch({ type: ImageInputActionType.SET_SECTION, payload }),
        [],
    );

    const handleSetSearch = React.useCallback(
        (payload: string) => dispatch({ type: ImageInputActionType.SET_SEARCH, payload }),
        [],
    );

    const currentImage = React.useMemo(
        () =>
            value
                ? libraryFiles.find((file) => file?.imageId === value) ??
                  (uploadedImage?.imageId === value ? uploadedImage : undefined) ??
                  defaultImage
                : undefined,
        [value, libraryFiles, uploadedImage, defaultImage],
    );

    return {
        currentImage,
        currentSection,
        handleResizeImageChanged,
        handleCloseModal,
        handleImageClick,
        handleOpenModal,
        handleSetSearch,
        handleSetSection,
        handleUploadImage,
        intl,
        isLibraryLoading: getFilesResponse.isLoading,
        isModalOpen,
        isUploadLoading,
        libraryFiles,
        resize,
        rowVirtualizer,
        search,
        disableResize,
        uploadError,
        virtualListRef,
    };
};
